import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, Mail, Lock, User, Phone } from 'lucide-react';
import PasswordStrengthIndicator from '../../components/shared/PasswordStrengthIndicator';
import { validateRegistrationPassword } from '../../utils/passwordValidation';
import { signInWithGoogle, signInWithFacebook, isOAuthConfigured } from '../../services/oauthService';

const Register = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    user_type: 'client',
    agreeToTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});

  const { register, oauthLogin } = useAuth();
  const navigate = useNavigate();
  const [oauthLoading, setOAuthLoading] = useState({ google: false, facebook: false });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    
    // Clear field-specific errors when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    // Only allow numbers
    const numericValue = value.replace(/\D/g, '');
    setFormData({
      ...formData,
      [name]: numericValue,
    });
    
    // Clear field-specific errors when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) {
      return;
    }
    
    setLoading(true);
    setError('');
    setErrors({});

    try {
      // Strong password validation
      const passwordValidation = validateRegistrationPassword(
        formData.password,
        formData.confirmPassword
      );
      
      if (!passwordValidation.isValid) {
        setErrors(passwordValidation.errors);
        setError('Please fix the password errors below');
        setLoading(false);
        return;
      }

      if (!formData.agreeToTerms) {
        setError('Please agree to the terms and conditions');
        setLoading(false);
        return;
      }

      const { agreeToTerms, ...registerData } = formData;
      const result = await register(registerData);
      
      if (result.success) {
        // Redirect based on user type and subscription status
        if (registerData.user_type === 'vendor') {
          // For vendors, redirect to subscription page first
          navigate('/vendor/subscription');
        } else if (registerData.user_type === 'client') {
          // For clients, redirect to client dashboard
          navigate('/dashboard');
        } else {
          // For admins or others, redirect to admin panel
          navigate('/admin');
        }
      } else {
        // Handle different types of errors
        if (result.error && typeof result.error === 'object') {
          // Backend validation errors
          setErrors(result.error);
          setError('Please fix the errors below');
        } else {
          // General error message
          setError(result.error || 'Registration failed');
        }
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (oauthLoading.google || loading) return;
    
    setOAuthLoading(prev => ({ ...prev, google: true }));
    setError('');
    setErrors({});

    try {
      const oauthData = await signInWithGoogle();
      const result = await oauthLogin(oauthData, formData.user_type);
      
      if (result.success) {
        // Redirect based on user type and subscription status
        if (formData.user_type === 'vendor') {
          navigate('/vendor/subscription');
        } else if (formData.user_type === 'client') {
          navigate('/dashboard');
        } else {
          navigate('/admin');
        }
      } else {
        if (result.error && typeof result.error === 'object') {
          setErrors(result.error);
          setError('Please fix the errors below');
        } else {
          setError(result.error || 'Google sign in failed');
        }
      }
    } catch (err) {
      setError(err.message || 'Google sign in failed. Please try again.');
    } finally {
      setOAuthLoading(prev => ({ ...prev, google: false }));
    }
  };

  const handleFacebookSignIn = async () => {
    if (oauthLoading.facebook || loading) return;
    
    setOAuthLoading(prev => ({ ...prev, facebook: true }));
    setError('');
    setErrors({});

    try {
      const oauthData = await signInWithFacebook();
      const result = await oauthLogin(oauthData, formData.user_type);
      
      if (result.success) {
        // Redirect based on user type and subscription status
        if (formData.user_type === 'vendor') {
          navigate('/vendor/subscription');
        } else if (formData.user_type === 'client') {
          navigate('/dashboard');
        } else {
          navigate('/admin');
        }
      } else {
        if (result.error && typeof result.error === 'object') {
          setErrors(result.error);
          setError('Please fix the errors below');
        } else {
          setError(result.error || 'Facebook sign in failed');
        }
      }
    } catch (err) {
      setError(err.message || 'Facebook sign in failed. Please try again.');
    } finally {
      setOAuthLoading(prev => ({ ...prev, facebook: false }));
    }
  };

  const oauthConfig = isOAuthConfigured();

  return (
    <div 
      className="min-h-screen flex flex-col justify-end sm:justify-center py-0 sm:py-12 sm:px-6 lg:px-8 relative"
      style={{
        backgroundImage: 'url(/images/01_-_HERO_1.webp)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Dark overlay for better readability */}
      <div className="absolute inset-0 bg-black/50"></div>
      
      <div className="hidden sm:block sm:mx-auto sm:w-full sm:max-w-md relative z-10 mb-8">
        <Link to="/" className="flex justify-center items-center mb-6">
          <img 
            src="/images/VendaLOGO1.png" 
            alt="Venda Logo" 
            className="h-16 w-auto"
          />
        </Link>
        <h2 className="text-center text-3xl font-medium text-white">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-white">
          Or{' '}
          <Link
            to="/login"
            className="font-medium text-white hover:text-gray-200 underline"
          >
            sign in to your existing account
          </Link>
        </p>
      </div>

      <div className="w-full sm:mx-auto sm:w-full sm:max-w-md relative z-10 animate-slideUp max-h-[90vh] overflow-y-auto">
        <div className="bg-white py-8 px-6 shadow-2xl rounded-t-3xl sm:rounded-2xl sm:px-10 border-t border-gray-100 sm:border">
          {/* Mobile Header */}
          <div className="sm:hidden mb-6 text-center">
            <Link to="/" className="flex justify-center items-center mb-4">
              <img 
                src="/images/VendaLOGO1.png" 
                alt="Venda Logo" 
                className="h-12 w-auto"
              />
            </Link>
            <h2 className="text-2xl font-medium text-black mb-2">
              Create your account
            </h2>
            <p className="text-sm text-gray-600">
              Or{' '}
              <Link
                to="/login"
                className="font-light text-black hover:text-gray-600 underline"
              >
                sign in to your existing account
              </Link>
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            {/* User Type Selection */}
            <div>
              <label className="block text-sm font-light text-black mb-1">I am a:</label>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <label className="relative">
                  <input
                    type="radio"
                    name="user_type"
                    value="client"
                    checked={formData.user_type === 'client'}
                    onChange={handleChange}
                    disabled={loading}
                    className="sr-only"
                  />
                  <div className={`p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                    formData.user_type === 'client'
                      ? 'border-black bg-gray-50 text-black'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}>
                    <div className="text-center">
                      <div className="font-medium">Client</div>
                      <div className="text-sm text-gray-500">Looking for vendors</div>
                    </div>
                  </div>
                </label>
                <label className="relative">
                  <input
                    type="radio"
                    name="user_type"
                    value="vendor"
                    checked={formData.user_type === 'vendor'}
                    onChange={handleChange}
                    disabled={loading}
                    className="sr-only"
                  />
                  <div className={`p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                    formData.user_type === 'vendor'
                      ? 'border-black bg-gray-50 text-black'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}>
                    <div className="text-center">
                      <div className="font-medium">Vendor</div>
                      <div className="text-sm text-gray-500">Offering services</div>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-light text-black mb-1">
                  First name
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="first_name"
                    name="first_name"
                    type="text"
                    autoComplete="given-name"
                    required
                    value={formData.first_name}
                    onChange={handleChange}
                    disabled={loading}
                    className={`input pl-10 ${errors.first_name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                    placeholder="First name"
                  />
                  {errors.first_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.first_name[0]}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="last_name" className="block text-sm font-light text-black mb-1">
                  Last name
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="last_name"
                    name="last_name"
                    type="text"
                    autoComplete="family-name"
                    required
                    value={formData.last_name}
                    onChange={handleChange}
                    disabled={loading}
                    className={`input pl-10 ${errors.last_name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                    placeholder="Last name"
                  />
                  {errors.last_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.last_name[0]}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-light text-black mb-1">
                Email address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  className={`input pl-10 ${errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email[0]}</p>
                )}
              </div>
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-light text-black mb-1">
                Phone number
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="tel"
                  required
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  onKeyPress={(e) => {
                    // Prevent non-numeric characters
                    if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                      e.preventDefault();
                    }
                  }}
                  disabled={loading}
                  className={`input pl-10 ${errors.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                  placeholder="Enter your phone number"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone[0]}</p>
                )}
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-light text-black mb-1">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  className={`input pl-10 pr-10 ${errors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                  placeholder="Create a password"
                />
                <button
                  type="button"
                  disabled={loading}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">
                  {typeof errors.password === 'string' ? errors.password : errors.password[0]}
                </p>
              )}
              {/* Password Strength Indicator */}
              <PasswordStrengthIndicator password={formData.password} className="mt-2" />
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-light text-black mb-1">
                Confirm password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={loading}
                  className={`input pl-10 pr-10 ${errors.confirmPassword ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  disabled={loading}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {typeof errors.confirmPassword === 'string' ? errors.confirmPassword : errors.confirmPassword[0]}
                </p>
              )}
            </div>

            {/* Terms Agreement */}
            <div className="flex items-center">
              <input
                id="agreeToTerms"
                name="agreeToTerms"
                type="checkbox"
                checked={formData.agreeToTerms}
                onChange={handleChange}
                disabled={loading}
                className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <label htmlFor="agreeToTerms" className="ml-2 block text-sm text-black">
                I agree to the{' '}
                <Link to="/terms" className="text-black hover:text-gray-600 underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-black hover:text-gray-600 underline">
                  Privacy Policy
                </Link>
              </label>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center py-3 bg-black text-white font-light rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Creating account...
                  </div>
                ) : (
                  'Create account'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-black">Or continue with</span>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={oauthLoading.google || loading || !oauthConfig.google}
                className="w-full inline-flex justify-center items-center py-2 px-4 border-2 border-black rounded-md shadow-sm bg-white text-sm font-light text-black hover:bg-black hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {oauthLoading.google ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    <span className="ml-2">Google</span>
                  </>
                )}
              </button>

              {/* Facebook button hidden for now */}
              {/* <button
                type="button"
                onClick={handleFacebookSignIn}
                disabled={oauthLoading.facebook || loading || !oauthConfig.facebook}
                className="w-full inline-flex justify-center items-center py-2 px-4 border-2 border-black rounded-md shadow-sm bg-white text-sm font-light text-black hover:bg-black hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {oauthLoading.facebook ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    <span className="ml-2">Facebook</span>
                  </>
                )}
              </button> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
